<template>
  <section
    class="billing-email-control-panel"
    :aria-labelledby="'billing-email-control-title-' + billingMonth"
    :aria-busy="loading.toString()"
  >
    <header class="control-header">
      <button
        type="button"
        class="control-toggle"
        :aria-expanded="detailsExpanded.toString()"
        :aria-controls="'billing-email-control-details-' + billingMonth"
        @click="detailsExpanded = !detailsExpanded"
      >
        <span class="control-heading">
          <md-icon class="control-heading-icon" aria-hidden="true">mail_outline</md-icon>
          <span>
            <span :id="'billing-email-control-title-' + billingMonth" class="control-title">請求メール送信</span>
            <span class="control-month">参照中の請求月：{{ billingMonthLabel }}</span>
          </span>
        </span>
        <span class="control-toggle-summary">
          <md-progress-spinner
            v-if="loading && !detailsExpanded"
            class="control-inline-spinner"
            :md-diameter="20"
            :md-stroke="2"
            md-mode="indeterminate"
            aria-hidden="true"
          ></md-progress-spinner>
          <span
            v-else-if="control && !detailsExpanded"
            class="status-badge collapsed-status"
            :class="'status-' + control.status"
          >
            {{ statusLabel }}
          </span>
          <span v-if="loading" class="visually-hidden">送信状態を読み込み中</span>
          <span class="visually-hidden">{{ detailsExpanded ? '折り畳む' : '展開する' }}</span>
          <md-icon class="control-toggle-icon" aria-hidden="true">
            {{ detailsExpanded ? 'expand_less' : 'expand_more' }}
          </md-icon>
        </span>
      </button>
      <md-button class="control-refresh" :disabled="busy" @click="loadControl()">
        <md-icon>refresh</md-icon>
        <span>状態を更新</span>
      </md-button>
    </header>

    <flash-message v-if="errorMessages.length" :errors="errorMessages"></flash-message>

    <div
      v-show="detailsExpanded"
      :id="'billing-email-control-details-' + billingMonth"
      class="control-details"
    >
      <loading-spinner v-if="loading"></loading-spinner>
      <template v-else-if="control">
        <div class="control-overview">
          <div>
            <div class="summary-label">送信状態</div>
            <div class="status-line">
              <span class="status-badge" :class="'status-' + control.status">{{ statusLabel }}</span>
              <span v-if="isHeld" class="held-state-message">
                <md-icon aria-hidden="true">block</md-icon>
                <span>停止中は自動送信されません</span>
              </span>
              <span v-else class="schedule">
                <span class="schedule-label">送信予定</span>
                {{ formatDateTime(control.scheduled_send_at) }}
              </span>
            </div>
          </div>

          <div v-if="hasKnownStatus" class="control-action">
            <md-button v-if="isWaiting" class="md-raised md-accent" :disabled="busy" @click="openHoldDialog">
              <span>メール自動送信を停止する</span>
            </md-button>
            <md-button v-if="isHeld" class="md-raised md-primary" :disabled="busy" @click="openReleaseDialog">
              <span>停止を解除する</span>
            </md-button>
            <span v-if="isOpen" class="open-help">
              <md-icon>lock</md-icon>
              <span>送信開始後の停止・再停止はできません。</span>
            </span>
          </div>
        </div>

        <div class="send-overview">
          <div>
            <div class="count-summary" aria-live="polite">
              <span>未送信 <strong>{{ control.issued_count }}件</strong></span>
              <span>送信中 <strong>{{ control.sending_count }}件</strong></span>
              <span>送信済み <strong>{{ control.sent_count }}件</strong></span>
            </div>
            <p v-if="isHeld" class="send-help">送信するには、先に「停止を解除する」を操作してください。</p>
          </div>
          <md-button class="md-raised md-primary send-button" :disabled="!canSend" @click="openSendDialog">
            未送信メールを送信する
          </md-button>
        </div>

        <div v-if="control.latest_send" class="latest-send" aria-live="polite">
          <div class="history-meta">
            <span>最新の送信依頼：{{ formatDateTime(control.latest_send.requested_at) }}</span>
            <span>実行者：{{ control.latest_send.operator }}</span>
          </div>
          <p :class="{'send-failed': latestSendFailed}">{{ latestSendLabel }}</p>
          <p v-if="latestSendFailed">状態と未送信件数を更新して確認し、必要に応じて再度送信してください。</p>
        </div>

        <div class="scope-notice">
          <md-icon>info</md-icon>
          <div>
            <span class="scope-label">操作対象：</span>
            <span>法人名などの検索条件に関係なく、{{ billingMonthLabel }}の自動メール送信対象全体に反映されます。</span>
          </div>
        </div>

        <div class="history" v-if="control.held_at">
          <div class="history-meta">
            <div v-if="isHeld" class="history-item">
              <span class="history-label">停止前の送信予定日時</span>
              <span>{{ formatDateTime(control.scheduled_send_at) }}</span>
            </div>
            <div class="history-item">
              <span class="history-label">停止日時</span>
              <span>{{ formatDateTime(control.held_at) }}</span>
            </div>
            <div class="history-item">
              <span class="history-label">停止者</span>
              <span>{{ control.held_by }}</span>
            </div>
            <template v-if="control.released_at">
              <div class="history-item">
                <span class="history-label">解除日時</span>
                <span>{{ formatDateTime(control.released_at) }}</span>
              </div>
              <div class="history-item">
                <span class="history-label">解除者</span>
                <span>{{ control.released_by }}</span>
              </div>
            </template>
          </div>
          <div class="reason">
            <span class="history-label">停止理由</span>
            <span class="reason-text">{{ control.hold_reason }}</span>
          </div>
        </div>
      </template>
    </div>

    <md-dialog :md-active.sync="sendDialogActive" :md-fullscreen="false">
      <md-dialog-title>未送信メールを送信する</md-dialog-title>
      <md-dialog-content>
        <p><strong>{{ billingMonthLabel }}：未送信 {{ control ? control.issued_count : 0 }}件</strong></p>
        <p>検索条件に関係なく、対象月全体の未送信メールを送信します。</p>
        <p>0円の請求書や郵送が選択されている請求書は送信しません。</p>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button :disabled="mutating" @click="sendDialogActive = false">キャンセル</md-button>
        <md-button class="md-primary" :disabled="!canSend" @click="sendPending">送信する</md-button>
      </md-dialog-actions>
    </md-dialog>

    <md-dialog :md-active.sync="holdDialogActive" :md-fullscreen="false">
      <md-dialog-title>メール自動送信を停止する</md-dialog-title>
      <md-dialog-content>
        <p>
          {{ billingMonthLabel }}の自動メール送信対象全体を停止します。
          法人名や現在の検索条件による絞り込みは適用されません。
        </p>
        <md-field :class="{'md-invalid': holdReasonInvalid || serverReasonError}">
          <label>停止理由</label>
          <md-textarea
            v-model="holdForm.reason"
            :maxlength="holdReasonMaxLength"
            required
            :disabled="mutating"
          ></md-textarea>
          <span class="md-error" v-if="serverReasonError">{{ serverReasonError }}</span>
          <span class="md-error" v-else-if="!$v.holdForm.reason.required">停止理由を入力してください。</span>
          <span class="md-error" v-else-if="!$v.holdForm.reason.maxLength">
            停止理由は{{ holdReasonMaxLength }}文字以内で入力してください。
          </span>
        </md-field>
        <div class="character-count">{{ holdForm.reason.length }} / {{ holdReasonMaxLength }}文字</div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button :disabled="mutating" @click="closeHoldDialog">キャンセル</md-button>
        <md-button class="md-primary" :disabled="mutating" @click="hold">
          <span>停止する</span>
        </md-button>
      </md-dialog-actions>
    </md-dialog>

    <md-dialog :md-active.sync="releaseDialogActive" :md-fullscreen="false">
      <md-dialog-title>メール自動送信の停止を解除する</md-dialog-title>
      <md-dialog-content>
        <p>{{ billingMonthLabel }}の停止を解除します。</p>
        <p>
          送信予定日時より前の場合は予定日時まで待機し、予定日時以降の場合は発行済みの未送信メールを送信します。
        </p>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button :disabled="mutating" @click="releaseDialogActive = false">キャンセル</md-button>
        <md-button class="md-primary" :disabled="mutating" @click="release">
          <span>解除する</span>
        </md-button>
      </md-dialog-actions>
    </md-dialog>

    <md-snackbar
      :md-active.sync="showSnackBar"
      :md-duration="snackbarDuration"
      @md-closed="showSnackBar = false"
    >
      <span>{{ successMessage }}</span>
      <md-button class="md-primary" @click="showSnackBar = false">閉じる</md-button>
    </md-snackbar>
  </section>
</template>

<script>
import FlashMessage                                      from '../../FlashMessage';
import LoadingSpinner                                    from '../../LoadingSpinner';
import Common                                            from '../../../modules/common';
import CommonMixin                                       from '../../../mixins/commonMixin';
import {apiClientWithManagerToken as apiClient, apiList} from '../../../modules/apiClient';
import {validationMixin}                                 from 'vuelidate';
import {maxLength, required}                             from 'vuelidate/lib/validators';

const STATUS_WAITING = 'waiting';
const STATUS_HELD = 'held';
const STATUS_OPEN = 'open';
const HOLD_REASON_MAX_LENGTH = 300;
const STATUS_LABELS = {
  [STATUS_WAITING]: '送信予定時刻前',
  [STATUS_HELD]   : '自動送信停止中',
  [STATUS_OPEN]   : '送信可能',
};

export default {
  name      : 'BillingEmailControlPanel',
  components: {FlashMessage, LoadingSpinner},
  mixins    : [CommonMixin, validationMixin],
  props     : {
    billingMonth: {
      type    : String,
      required: true,
    },
    refreshToken: {
      type   : Number,
      default: 0,
    },
  },
  data      : () => ({
    control           : null,
    loading           : false,
    mutating          : false,
    holdDialogActive  : false,
    releaseDialogActive: false,
    sendDialogActive  : false,
    holdForm          : {
      reason: '',
    },
    serverReasonError : null,
    errorMessages     : [],
    loadSequence      : 0,
    detailsExpanded   : true,
    holdReasonMaxLength: HOLD_REASON_MAX_LENGTH,
  }),
  validations: {
    holdForm: {
      reason: {
        required,
        maxLength: maxLength(HOLD_REASON_MAX_LENGTH),
      },
    },
  },
  computed: {
    /**
     * 最新の取得結果から送信操作の可否を判定する。
     * @returns {boolean}
     */
    canSend() {
      return !this.busy && this.control !== null && this.isOpen && this.control.issued_count > 0;
    },
    /**
     * 最新依頼に操作の見直しが必要か判定する。
     * @returns {boolean}
     */
    latestSendFailed() {
      return this.control && this.control.latest_send
        ? ['failed', 'blocked'].includes(this.control.latest_send.status) : false;
    },
    /**
     * メール送信完了と区別して依頼の処理状況を表示する。
     * @returns {string}
     */
    latestSendLabel() {
      const send = this.control && this.control.latest_send;
      if (!send) {
        return '';
      }
      const labels = {
        queued: '送信依頼を受付済みです。状態を更新して結果を確認してください。',
        processing: 'メール送信処理へ登録中です。',
        completed: `メール送信処理への登録が完了しました（${send.queued_count}件）。メールの送信完了は上の件数で確認してください。`,
        failed: '送信依頼の処理に失敗しました。一部は登録済みの場合があります。',
        blocked: '予定時刻前または自動送信停止中のため、送信処理への登録を見送りました。',
      };
      return labels[send.status] || '依頼の処理状態を確認できません。状態を更新してください。';
    },
    /**
     * 対象請求月の表示名を取得する。
     *
     * @returns {string}
     */
    billingMonthLabel() {
      return this.datetimeFormat(this.billingMonth + '01', 'YYYY年M月');
    },
    /**
     * 状態取得を再実行する識別子を取得する。
     *
     * @returns {string}
     */
    requestKey() {
      return this.billingMonth + ':' + this.refreshToken;
    },
    /**
     * 状態表示名を取得する。
     *
     * @returns {string}
     */
    statusLabel() {
      return STATUS_LABELS[this.control.status] || '不明';
    },
    /**
     * 送信予定時刻前か判定する。
     *
     * @returns {boolean}
     */
    isWaiting() {
      return this.control.status === STATUS_WAITING;
    },
    /**
     * 自動送信停止中か判定する。
     *
     * @returns {boolean}
     */
    isHeld() {
      return this.control.status === STATUS_HELD;
    },
    /**
     * 送信可能状態か判定する。
     *
     * @returns {boolean}
     */
    isOpen() {
      return this.control.status === STATUS_OPEN;
    },
    /**
     * APIから既知の状態を取得できたか判定する。
     *
     * @returns {boolean}
     */
    hasKnownStatus() {
      return Object.prototype.hasOwnProperty.call(STATUS_LABELS, this.control.status);
    },
    /**
     * 状態取得または更新中か判定する。
     *
     * @returns {boolean}
     */
    busy() {
      return this.loading || this.mutating;
    },
    /**
     * 停止理由が入力エラーか判定する。
     *
     * @returns {boolean}
     */
    holdReasonInvalid() {
      return this.$v.holdForm.reason.$dirty && this.$v.holdForm.reason.$invalid;
    },
  },
  watch: {
    /**
     * 別の請求月に対する確認操作の持ち越しを防ぐ。
     * @returns {void}
     */
    billingMonth() {
      this.sendDialogActive = false;
      this.holdDialogActive = false;
      this.releaseDialogActive = false;
    },
    /**
     * 表示対象または再取得指示の変更時に状態を取得する。
     */
    requestKey: {
      immediate: true,
      /**
       * 表示対象の状態を取得する。
       *
       * @returns {void}
       */
      handler() {
        this.loadControl();
      },
    },
  },
  methods: {
    /**
     * 未送信メールの送信確認画面を開く。
     * @returns {void}
     */
    openSendDialog() {
      if (this.canSend) {
        this.sendDialogActive = true;
      }
    },
    /**
     * 未送信メールの送信を依頼し、受付後の状態を取得する。
     * @returns {Promise<void>}
     */
    async sendPending() {
      if (!this.canSend || !this.sendDialogActive) {
        return;
      }
      const billingMonth = this.billingMonth;
      this.mutating = true;
      this.errorMessages = [];
      this.successMessage = null;
      this.showSnackBar = false;

      try {
        const response = await apiClient.post(this.controlUrl(apiList.jon.billing_email_controls.send));
        if (billingMonth !== this.billingMonth) {
          return;
        }
        if (typeof response.data.accepted !== 'boolean') {
          throw new Error('送信依頼の受付結果が不明です');
        }
        this.sendDialogActive = false;
        this.successMessage = response.data.accepted
          ? '送信依頼を受け付けました。状態を更新して結果を確認してください。'
          : '送信対象はありません。';
        this.showSnackBar = true;
        await this.loadControl(false);
      } catch (error) {
        if (billingMonth !== this.billingMonth) {
          return;
        }
        this.sendDialogActive = false;
        await this.handleMutationError(error, '送信依頼の受付結果を確認できませんでした。状態を更新して確認してください。');
      } finally {
        this.mutating = false;
      }
    },
    /**
     * 請求月の送信制御URLを生成する。
     *
     * @param {string} template URLテンプレート
     * @returns {string}
     */
    controlUrl(template) {
      return template.replace(':billing_month', this.billingMonth);
    },
    /**
     * 日時を管理画面向けに整形する。
     *
     * @param {?string} value ISO 8601日時
     * @returns {string}
     */
    formatDateTime(value) {
      return value ? this.datetimeFormat(value, 'YYYY年M月D日 H:mm:ss') : '-';
    },
    /**
     * 請求月のメール送信状態を取得する。
     *
     * @param {boolean} clearMessages メッセージを初期化するか
     * @returns {Promise<void>}
     */
    async loadControl(clearMessages = true) {
      const loadSequence = ++this.loadSequence;
      if (clearMessages) {
        this.errorMessages = [];
        this.successMessage = null;
        this.showSnackBar = false;
      }
      this.loading = true;

      try {
        const response = await apiClient.get(this.controlUrl(apiList.jon.billing_email_controls.detail));

        if (loadSequence !== this.loadSequence) {
          return;
        }

        this.control = response.data;

        if (!this.hasKnownStatus) {
          this.errorMessages = [Common.messages.billings.emailControl.unknownStatus];
        }
      } catch (error) {
        if (loadSequence !== this.loadSequence) {
          return;
        }

        this.control = null;
        const message = Common.messages.billings.emailControl.getError;
        this.errorMessages = clearMessages ? [message] : this.errorMessages.concat(message);
      } finally {
        if (loadSequence === this.loadSequence) {
          this.loading = false;
        }
      }
    },
    /**
     * 停止理由入力ダイアログを開く。
     *
     * @returns {void}
     */
    openHoldDialog() {
      this.holdForm.reason = '';
      this.serverReasonError = null;
      this.$v.$reset();
      this.holdDialogActive = true;
    },
    /**
     * 停止理由入力ダイアログを閉じる。
     *
     * @returns {void}
     */
    closeHoldDialog() {
      this.holdDialogActive = false;
      this.$v.$reset();
    },
    /**
     * 停止解除確認ダイアログを開く。
     *
     * @returns {void}
     */
    openReleaseDialog() {
      this.releaseDialogActive = true;
    },
    /**
     * 請求月のメール自動送信を停止する。
     *
     * @returns {Promise<void>}
     */
    async hold() {
      this.$v.$touch();
      this.serverReasonError = null;
      if (this.$v.$invalid) {
        return;
      }

      this.mutating = true;
      this.errorMessages = [];
      this.successMessage = null;
      this.showSnackBar = false;

      try {
        const response = await apiClient.post(
          this.controlUrl(apiList.jon.billing_email_controls.hold),
          {reason: this.holdForm.reason},
        );
        this.control = response.data;
        this.holdDialogActive = false;
        this.successMessage = Common.messages.billings.emailControl.holdSuccess;
        this.showSnackBar = true;
        this.$v.$reset();
      } catch (error) {
        await this.handleMutationError(error, Common.messages.billings.emailControl.holdError);
      } finally {
        this.mutating = false;
      }
    },
    /**
     * 請求月のメール自動送信停止を解除する。
     *
     * @returns {Promise<void>}
     */
    async release() {
      this.mutating = true;
      this.errorMessages = [];
      this.successMessage = null;
      this.showSnackBar = false;

      try {
        const response = await apiClient.post(this.controlUrl(apiList.jon.billing_email_controls.release));
        this.control = response.data;
        this.releaseDialogActive = false;
        this.successMessage = Common.messages.billings.emailControl.releaseSuccess;
        this.showSnackBar = true;
      } catch (error) {
        await this.handleMutationError(error, Common.messages.billings.emailControl.releaseError);
      } finally {
        this.mutating = false;
      }
    },
    /**
     * 更新APIのエラーを画面へ反映する。
     *
     * @param {Error} error APIエラー
     * @param {string} fallbackMessage 既定メッセージ
     * @returns {Promise<void>}
     */
    async handleMutationError(error, fallbackMessage) {
      const response = error.response;
      const status = response ? response.status : null;
      const data = response ? response.data : {};

      if (status === 422 && data.errors) {
        this.serverReasonError = data.errors.reason || null;
        const otherErrors = Object.keys(data.errors)
          .filter(key => key !== 'reason')
          .map(key => data.errors[key]);
        this.errorMessages = otherErrors;
        return;
      }

      const message = status === 409 && data.message ? data.message : fallbackMessage;
      this.errorMessages = [message];
      await this.loadControl(false);
    },
  },
};
</script>

<style lang="scss" scoped>
.billing-email-control-panel {
  margin: 20px 0 24px;
  padding: 18px 20px;
  border-top: 1px solid rgba(0, 0, 0, 0.18);
  border-bottom: 1px solid rgba(0, 0, 0, 0.18);
  background-color: rgba(0, 0, 0, 0.018);
}

.control-header,
.control-overview,
.control-heading,
.status-line,
.control-toggle-summary,
.held-state-message,
.open-help {
  display: flex;
  align-items: center;
}

.control-overview {
  justify-content: space-between;
  gap: 20px;
}

.send-overview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px 20px;
  margin-top: 18px;
}

.count-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
}

.send-help,
.latest-send {
  font-size: 13px;
  line-height: 1.6;
}

.send-help {
  margin: 8px 0 0;
  color: rgba(0, 0, 0, 0.68);
}

.send-button {
  flex: 0 0 auto;
  margin: 0;
}

.latest-send {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  margin-top: 16px;
  padding-top: 12px;
}

.latest-send p {
  margin: 6px 0 0;
}

.send-failed {
  color: #c62828;
  font-weight: 500;
}

.control-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.control-toggle {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
  margin: -6px 0 -6px -8px;
  padding: 6px 8px;
  border: 0;
  border-radius: 2px;
  outline: none;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.control-toggle:hover,
.control-toggle:focus {
  background-color: rgba(0, 0, 0, 0.04);
}

.control-heading {
  min-width: 0;
}

.control-heading-icon {
  margin: 0 12px 0 0;
  color: rgba(0, 0, 0, 0.54);
}

.control-title {
  display: block;
  margin: 0;
  font-size: 20px;
  font-weight: 400;
  line-height: 1.4;
}

.control-month {
  display: block;
  margin-top: 2px;
  color: rgba(0, 0, 0, 0.54);
  font-size: 13px;
}

.control-refresh {
  flex: 0 0 auto;
  margin: 0 -8px 0 0;
  color: rgba(0, 0, 0, 0.68);
}

.control-toggle-summary {
  flex: 0 0 auto;
  gap: 12px;
  margin-left: 16px;
}

.control-toggle-icon {
  margin: 0;
  color: rgba(0, 0, 0, 0.54);
}

.control-inline-spinner {
  flex: 0 0 auto;
  margin: 0;
}

.collapsed-status {
  font-size: 13px;
  white-space: nowrap;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  border: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}

.control-overview {
  margin-top: 18px;
}

.status-line {
  flex-wrap: wrap;
  gap: 12px 16px;
}

.held-state-message {
  color: #c62828;
  font-size: 14px;
  font-weight: 500;
}

.held-state-message .md-icon {
  margin: 0 6px 0 0;
  color: #c62828;
  font-size: 18px !important;
}

.scope-notice {
  display: flex;
  align-items: center;
  margin: 16px 0 0;
  padding: 10px 12px;
  border-left: 3px solid #90a4ae;
  background-color: rgba(96, 125, 139, 0.08);
  color: rgba(0, 0, 0, 0.72);
  font-size: 13px;
  line-height: 1.6;
}

.scope-notice .md-icon {
  margin: 0 8px 0 0;
  color: #607d8b;
  font-size: 20px !important;
}

.scope-label {
  margin-right: 8px;
  font-weight: 500;
}

.summary-label {
  margin-bottom: 4px;
  color: rgba(0, 0, 0, 0.54);
  font-size: 13px;
}

.schedule {
  color: rgba(0, 0, 0, 0.72);
  font-size: 15px;
}

.schedule-label {
  margin-right: 8px;
  color: rgba(0, 0, 0, 0.54);
  font-size: 13px;
}

.status-badge {
  display: inline-block;
  padding: 5px 12px;
  border-radius: 2px;
  font-weight: 500;
  line-height: 1.4;
}

.status-waiting {
  color: #1565c0;
  background-color: #e3f2fd;
}

.status-held {
  color: #c62828;
  background-color: #ffebee;
}

.status-open {
  color: #2e7d32;
  background-color: #e8f5e9;
}

.control-action {
  flex: 0 0 auto;
}

.control-action .md-button {
  margin: 0;
}

.history {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  font-size: 13px;
  line-height: 1.6;
}

.history-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 24px;
}

.history-item {
  display: flex;
  gap: 8px;
}

.history-label {
  flex: 0 0 auto;
  color: rgba(0, 0, 0, 0.54);
}

.reason {
  display: flex;
  gap: 8px;
  margin-top: 6px;
}

.reason-text {
  white-space: pre-wrap;
}

.open-help {
  color: rgba(0, 0, 0, 0.54);
  font-size: 13px;
}

.open-help .md-icon {
  margin: 0 6px 0 0;
  color: rgba(0, 0, 0, 0.38);
  font-size: 20px !important;
}

.character-count {
  text-align: right;
  color: rgba(0, 0, 0, 0.54);
  font-size: 12px;
}

.md-dialog {
  width: 560px;
}

@media (max-width: 960px) {
  .control-overview {
    align-items: flex-start;
    flex-direction: column;
  }

}

@media (max-width: 600px) {
  .billing-email-control-panel {
    padding-right: 12px;
    padding-left: 12px;
  }

  .control-header {
    align-items: flex-start;
  }

  .control-toggle-summary {
    gap: 6px;
    margin-left: 8px;
  }

  .control-refresh span {
    display: none;
  }

  .schedule {
    width: 100%;
  }

  .control-action,
  .control-action .md-button {
    width: 100%;
  }

  .reason {
    flex-direction: column;
    gap: 2px;
  }
}
</style>
